import React from "react";
import { useForm } from "react-hook-form";

import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextControl,
  Form,
  Divider,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
} from "@tiny/components";

export interface SaveAsDialogProps {
  open: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  onErrorMessageClose: () => void;
  onExit: () => void;
  inputLabel?: string;
  title: string;
  subtitle?: string;
  name?: string;
  errorMessage?: string;
  maxLength: number;
}

function SaveAsDialog(props: SaveAsDialogProps) {
  const { open, onClose, onExit } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
      TransitionProps={{ onExit }}
    >
      <SaveAsDialogContent {...props} />
    </Dialog>
  );
}

function SaveAsDialogContent(props: SaveAsDialogProps) {
  const {
    onClose,
    inputLabel,
    onSave,
    title,
    subtitle,
    errorMessage,
    saving,
    onErrorMessageClose,
    maxLength,
  } = props;

  const form = useForm<{ name: string }>();

  const [autoGen, setAutoGen] = React.useState(false);

  const submitForm = (values: { name: string }) => {
    onSave?.(autoGen ? "" : values.name);
  };

  const save = () => {
    form.handleSubmit(submitForm)();
  };

  const onCbkClick = React.useCallback(() => {
    setAutoGen((old) => !old);
    onErrorMessageClose();
  }, [onErrorMessageClose]);

  return (
    <>
      <DialogTitle subtitle={subtitle} onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent paddingTop={3}>
        <Form form={form} onSubmit={submitForm}>
          {errorMessage && (
            <Box mb={5}>
              <Alert
                variant="filled"
                severity="error"
                size="small"
                onClose={onErrorMessageClose}
              >
                {errorMessage}
              </Alert>
            </Box>
          )}
          {!autoGen && (
            <TextControl
              name="name"
              label={inputLabel}
              defaultValue=""
              required
              maxLength={maxLength}
            />
          )}
          <Box mt={2}>
            <FormControlLabel
              control={<Checkbox color="primary" onClick={onCbkClick} />}
              label="Generate name automatically"
            />
          </Box>
        </Form>
        <Divider mt={4} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          loading={saving}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
}

export default SaveAsDialog;
